
import { useEffect, useState } from 'react'
import { useEnhancedBrandStore } from './stores/enhancedBrandStore'
import { useAuth, ProtectedRoute } from './contexts/AuthContext'
import { KPICards } from './components/kpi/KPICards'
import { SearchBar } from './components/filters/SearchBar'
import { InteractiveAnalyticsFilters } from './components/filters/InteractiveAnalyticsFilters'
import { BrandTable } from './components/table/BrandTable'
import { USMapSection } from './components/sections/USMapSection'
import { BrandProfileDrawer } from './components/layout/BrandProfileDrawer'
import { ThemeToggle } from './components/ui/ThemeToggle'
import { RefreshButton } from './components/ui/RefreshButton'
import { ScrollToTopButton } from './components/ui/ScrollToTopButton'
import { AuthModal } from './components/auth/AuthModal'
import { UserProfile } from './components/auth/UserProfile'
import { SettingsPanel } from './components/settings/SettingsPanel'
import { ParsedBrand } from './types/brand'
import { UserIcon } from '@heroicons/react/24/outline'
import { Stethoscope } from 'lucide-react'

function App() {
  const { loadData, loadFiltersFromURL, error } = useEnhancedBrandStore()
  const { isAuthenticated, hasPermission } = useAuth()
  const [selectedBrand, setSelectedBrand] = useState<ParsedBrand | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [currentView, setCurrentView] = useState<'dashboard' | 'settings'>('dashboard')

  useEffect(() => {
    // Only load data when authenticated with view_brands permission
    if (isAuthenticated && hasPermission('view_brands')) {
      loadData().then(() => {
        // Load filters from URL after data is loaded
        loadFiltersFromURL()
      })
    }
  }, [isAuthenticated, hasPermission, loadData, loadFiltersFromURL])

  const handleRowClick = (brand: ParsedBrand) => {
    setSelectedBrand(brand)
    setIsDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
    // Delay clearing the brand to allow for smooth close animation
    setTimeout(() => setSelectedBrand(null), 300)
  }


  const renderAuthenticatedContent = () => {
    if (currentView === 'settings') {
      return <SettingsPanel />
    }

    return (
      <>
        {/* Search Bar - Prominent placement at top */}
        <div className="mb-6">
          <SearchBar onRowClick={handleRowClick} />
        </div>
        
        {/* Overview KPIs - Colorful cards at top */}
        <div className="mb-6">
          <KPICards />
        </div>
        
        {/* US Geographic Map - Standalone section */}
        <div className="mb-6">
          <USMapSection />
        </div>
        
        {/* Interactive Analytics & Filters - Combined interface */}
        <div className="mb-6">
          <InteractiveAnalyticsFilters />
        </div>
        
        {/* Data Table */}
        <BrandTable 
          onRowClick={handleRowClick}
          onAddBrandClick={() => setCurrentView('settings')}
        />
      </>
    )
  }

  const renderLandingScreen = () => {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 text-center">
          {/* Logo and Branding */}
          <div className="space-y-4">
            <div className="mx-auto flex items-center justify-center w-20 h-20 bg-blue-100 dark:bg-blue-900/20 rounded-full">
              <Stethoscope className="h-10 w-10 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                Welcome to BrandScope
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Complete Brand Management Platform with AI-Powered Analytics
              </p>
            </div>
          </div>

          {/* Sign In Message */}
          <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Sign In Required
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Please sign in to access brand data, analytics, and management features.
            </p>
            

            <button
              onClick={() => setShowAuthModal(true)}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <UserIcon className="h-5 w-5" />
              <span>Sign In to Continue</span>
            </button>
          </div>

          {/* Features Preview */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div className="text-center space-y-2">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto">
                <span className="text-green-600 dark:text-green-400 text-lg">📊</span>
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white">Analytics</h4>
              <p className="text-gray-600 dark:text-gray-400">Interactive charts and insights</p>
            </div>
            <div className="text-center space-y-2">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mx-auto">
                <span className="text-purple-600 dark:text-purple-400 text-lg">🗺️</span>
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white">Geographic</h4>
              <p className="text-gray-600 dark:text-gray-400">US map with state breakdown</p>
            </div>
            <div className="text-center space-y-2">
              <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center mx-auto">
                <span className="text-orange-600 dark:text-orange-400 text-lg">📋</span>
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white">Management</h4>
              <p className="text-gray-600 dark:text-gray-400">Full CRUD operations</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Header - Fixed for better navigation */}
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button 
              onClick={() => {
                setCurrentView('dashboard')
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity group"
            >
              <Stethoscope className="h-8 w-8 text-blue-600 dark:text-blue-400 group-hover:scale-105 transition-transform" />
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                BrandScope
              </h1>
            </button>
            
            <div className="flex items-center space-x-4">
              {/* Authenticated User Controls */}
              {isAuthenticated ? (
                <>
                  <RefreshButton />
                  <ThemeToggle />
                  <UserProfile onSettingsClick={() => setCurrentView('settings')} />
                </>
              ) : (
                <>
                  {/* Clean interface for unauthenticated users */}
                  <ThemeToggle />
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    <UserIcon className="h-4 w-4" />
                    <span>Sign In</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <ProtectedRoute 
          requiredPermission="view_brands"
          fallback={renderLandingScreen()}
        >
          {/* Show errors only to authenticated users */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 rounded-xl">
              <strong>Error:</strong> {error}
            </div>
          )}
          {renderAuthenticatedContent()}
        </ProtectedRoute>
      </main>
      
      {/* Brand Profile Drawer - Only for authenticated users */}
      {isAuthenticated && (
        <BrandProfileDrawer
          brand={selectedBrand}
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
        />
      )}
      
      {/* Authentication Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
      />
      
      {/* Scroll to Top Button */}
      <ScrollToTopButton />
    </div>
  )
}

export default App